import { useMemo, useState } from 'react';
import { DSButton, DSTypography } from '@eiv2/design-system';
import {
  OrganizerEventFilter,
  EventRequiredState
} from '../components/remarketing/OrganizerEventFilter';
import { DashboardPage } from '../pages/DashboardPage';
import { FunnelPage } from '../pages/FunnelPage';
import { CheckoutsPage } from '../pages/CheckoutsPage';
import { EventsPage } from '../pages/EventsPage';
import { SectionCard } from '../components/SectionCard';
import { StatusChip } from '../components/StatusChip';
import { formatDateTime, formatNumber } from '../services/format';
import type { CheckoutSummary } from '../types/api';
import type { OrganizerDataApi } from './api';
import type { OrganizerAccess } from './session';

export type TrackingTab = 'overview' | 'funnel' | 'checkouts' | 'activities';

export function OrganizerTracking({
  tab,
  api,
  access
}: {
  tab: TrackingTab;
  api: OrganizerDataApi;
  access: OrganizerAccess;
}) {
  const [eventId, setEventId] = useState('');
  const scope = access.organization;
  return (
    <div>
      <div className="mb-6 max-w-xl">
        <OrganizerEventFilter
          scope={scope}
          required={scope.eventsTruncated}
          value={eventId}
          onChange={setEventId}
        />
      </div>
      {scope.eventsTruncated && !eventId ? (
        <EventRequiredState truncated />
      ) : (
        <TrackingContent
          key={`${tab}:${eventId}`}
          tab={tab}
          api={api}
          organizationId={scope.id}
          eventId={eventId || undefined}
        />
      )}
    </div>
  );
}

function TrackingContent({
  tab,
  api,
  organizationId,
  eventId
}: {
  tab: TrackingTab;
  api: OrganizerDataApi;
  organizationId: string;
  eventId?: string;
}) {
  const tracking = useMemo(
    () => api.tracking(organizationId, eventId),
    [api, organizationId, eventId]
  );
  const [selected, setSelected] = useState<CheckoutSummary | null>(null);
  if (selected)
    return <JourneyActivity row={selected} tracking={tracking} onBack={() => setSelected(null)} />;
  if (tab === 'overview') return <DashboardPage api={tracking} />;
  if (tab === 'funnel') return <FunnelPage api={tracking} />;
  if (tab === 'activities') return <EventsPage api={tracking} organizer />;
  return <CheckoutsPage api={tracking} onOpenCheckout={setSelected} organizer />;
}

/** Também abre a atividade de quem abandonou antes da criação do checkout. */
function JourneyActivity({
  row,
  tracking,
  onBack
}: {
  row: CheckoutSummary;
  tracking: ReturnType<OrganizerDataApi['tracking']>;
  onBack: () => void;
}) {
  const timeline = useMemo(
    () => ({
      listEvents: (params: Parameters<typeof tracking.listEvents>[0], signal?: AbortSignal) =>
        tracking.listEvents(
          {
            ...params,
            ...(row.journeyId
              ? { journeyId: row.journeyId }
              : { sessionId: row.sessionId, source: row.source })
          },
          signal
        )
    }),
    [tracking, row]
  );
  const initialWindow = useMemo(() => {
    // A listagem de atividades filtra received_at, enquanto lastEventAt vem
    // de occurred_at. Inclua o processamento da fila e a janela máxima da
    // timeline para não esconder um evento recebido com atraso.
    const to = Math.max(Date.parse(row.updatedAt), Date.parse(row.lastEventAt)) + 1000;
    const from = to - 30 * 86400000;
    return { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
  }, [row]);
  return (
    <div>
      <DSButton className="mb-4 w-auto" variant="secondary" onClick={onBack}>
        Voltar aos carrinhos
      </DSButton>
      <SectionCard title="Resumo da jornada" action={<StatusChip status={row.status} />}>
        <DSTypography as="p">
          {formatNumber(row.eventCount)} atividades registradas · última atividade em{' '}
          {formatDateTime(row.lastEventAt)}
        </DSTypography>
      </SectionCard>
      <div className="mt-6">
        <EventsPage api={timeline} initialWindow={initialWindow} organizer />
      </div>
    </div>
  );
}
